import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  useIonViewWillEnter,
  useIonViewWillLeave,
  IonButtons,
  IonBackButton,
  IonButton,
  IonIcon,
  IonFooter,
  useIonModal,
  useIonPopover,
  useIonLoading,
} from "@ionic/react";
import { useEffect, useContext, useRef, useMemo } from "react";
import { ellipsisVerticalCircle, ellipsisVerticalSharp } from "ionicons/icons";
import useState from "react-usestateref";
import Masonry from "react-masonry-css";
import SpikeGenSettings from "../components/SpikeGen/SpikeGenSettings";
import { SpikeGenImagesProps, SpikeGenProps } from "../interfaces/core";
import { AppContext } from "../components/AppContext";
import { presetColors } from "../lib/spike-pie/colors";
import startCreating from "../lib/spike-pie/startCreating";
import useForm from "../lib/useForm";
import SpikeGenPopover from "../components/SpikeGen/SpikeGenPopover";
import SpikeGenImage from "../components/SpikeGen/SpikeGenImage";
import { exportZip } from "../lib/saveCanvas";
import b64toBlob from "../lib/b64toBlob";
import { useTranslation } from "react-i18next";

const defaultSettings: SpikeGenProps = {
  smoothing: false, // anti-aliasing
  colorType: "solid",
  colorGradient: false,
  randomColors: true,
  detailedColorView: "primary",
  selectedColors: [],
  scale: 2,
  tileX: 2,
  tileY: 3,
  numberOfImages: 3,
};
const breakpointColumnsObj = {
  default: 3,
  820: 2,
  480: 1,
};

const SpikeGen: React.FC = () => {
  const appContext = useContext(AppContext);
  const { setTabBarHidden } = appContext.tabBar;
  const contentRef = useRef<HTMLIonContentElement | null>(null);

  const { t } = useTranslation();

  const [images, setImages, imagesRef] = useState<SpikeGenImagesProps[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);
  const settingsRef = useRef<any | undefined>();
  const disabled = isLoading || images.length >= 40;

  const { inputs, handleChange, resetForm } = useForm({
    smoothing: settings.smoothing,
    colorType: settings.colorType,
    colorGradient: settings.colorGradient,
    randomColors: settings.randomColors,
    detailedColorView: settings.detailedColorView,
    selectedColors: settings.selectedColors,
    scale: settings.scale,
    tileX: settings.tileX,
    tileY: settings.tileY,
    numberOfImages: settings.numberOfImages,
  } as SpikeGenProps);
  const [showLoading, hideLoading] = useIonLoading();
  const getSettings = async () => {
    setIsLoading(true);
    const res = await appContext?.store?.get("spike-pie-settings");
    if (res) {
      setSettings(res);
      settingsRef.current = res;
    }
    setIsLoading(false);
  };
  const getSavedImages = async () => {
    setIsLoading(true);
    const res = await appContext?.store?.get("spike-pie-images");
    if (res) {
      //   console.log(res.length);
      setImages(res);
    }
    setIsLoading(false);
  };

  useIonViewWillEnter(() => setTabBarHidden(true));
  useIonViewWillLeave(() => {
    setTabBarHidden(false);
  });

  const base_width = 400;
  const base_height = 400;

  const format = useMemo(
    () => ({
      baseWdith: base_width * inputs.scale,
      baseHeight: base_height * inputs.scale,
      width: base_width * inputs.tileX * inputs.scale,
      height: base_height * inputs.tileY * inputs.scale,
      smoothing: false, // anti-aliasing
      gradient: inputs.colorGradient, // Enable same set of color gradient
      multiColor: inputs.colorType === "multiColor" ? true : false,
      colorGradient: inputs.colorType === "multiColor" && inputs.colorGradient, // Origin and end point color hues are different
      scale: inputs.scale,
      numberOfImages: inputs.numberOfImages,
    }),
    [inputs]
  );
  const getColors = ({ names = [] }) => {
    let colorResult: any[] = [];
    if (names.length !== 0) {
      for (let i = 0; i < names.length; i++) {
        const name = names[i];
        const matches = presetColors.filter((c) => c.name === name);
        // console.log(matches.length);
        colorResult = colorResult.concat(matches);
      }
    }
    // console.log(colorResult.length);
    return colorResult.length > 0 ? colorResult : presetColors;
  };

  const handleGenerate = async () => {
    const colors = getColors({
      names: inputs.randomColors ? [] : inputs.selectedColors, // choose every color if gradient is true
    });
    const res = await startCreating({
      tiles: {
        x: inputs.tileX,
        y: inputs.tileY,
      },
      colors,
      format,
    });
    setImages((prev) => [...res, ...prev]);
    hideLoading();
  };

  const updateStore = async (results) => {
    setIsLoading(true);
    await appContext?.store?.set("spike-pie-images", results);
    setIsLoading(false);
  };
  const handleResetDefaultSettings = async () => {
    setSettings(defaultSettings);
    await appContext?.store?.set("spike-pie-settings", defaultSettings);
    dismiss();
    await getSettings();
  };
  const handleSaveSettings = async () => {
    await appContext?.store?.set("spike-pie-settings", inputs);
    dismiss();
    await getSettings();
  };

  const handleDismiss = () => {
    dismiss();
  };
  const handleDeleteImage = (id) => {
    let newImages = imagesRef.current;
    newImages = newImages.filter((i) => i.id !== id);
    setImages(newImages);
  };
  const handleDeleteAllImages = async () => {
    setImages([]);
    dismissPopover();
  };
  const handelDownloadAllImages = async () => {
    setIsLoading(true);
    await exportZip(
      "spike-pie-generator",
      "spike-pie",
      images.map((img) => b64toBlob(img.full))
    ).then(() => {
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    });
  };

  const [presentPopover, dismissPopover] = useIonPopover(SpikeGenPopover, {
    imageLength: imagesRef.current.length,
    onHide: () => dismissPopover(),
    handleDeleteAllImages,
    handelDownloadAllImages,
  });
  const [present, dismiss] = useIonModal(SpikeGenSettings, {
    inputs,
    resetForm,
    handleChange,
    handleDismiss,
    handleSaveSettings,
    handleResetDefaultSettings,
  });

  useEffect(() => {
    if (typeof appContext.store !== undefined) {
      getSavedImages();
      getSettings();
    }
  }, [appContext.store]);

  useEffect(() => {
    updateStore(images);
  }, [images]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" />
          </IonButtons>
          <IonButtons slot="end">
            <IonButton
              onClick={(e) =>
                presentPopover({
                  event: e.nativeEvent,
                })
              }
            >
              <IonIcon
                icon={ellipsisVerticalCircle}
                ios={ellipsisVerticalCircle}
                md={ellipsisVerticalSharp}
              />
            </IonButton>
          </IonButtons>
          <IonTitle>{t(`Spike Pie Generator.title`)}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent ref={contentRef} scrollEvents>
        {images.length >= 40 && (
          <p className="text-center py-4 text-rose-900 bg-rose-50 dark:bg-rose-900 dark:text-rose-50">
            {t(`Spike Pie Generator.max-error`)}
          </p>
        )}
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="flex max-w-7xl mx-auto gap-4 md:gap-6 my-4 md:my-6 sm:px-6"
          columnClassName="flex flex-col gap-4 md:gap-6"
        >
          {images.length > 0 &&
            images.map((image) => (
              <SpikeGenImage
                key={image.id}
                onDelete={() => handleDeleteImage(image.id)}
                setIsLoading={setIsLoading}
                image={image}
              />
            ))}
        </Masonry>
        {images.length === 0 && (
          <div className="w-full flex justify-center items-center h-screen max-w-7xl mx-auto">
            <div className="w-full pb-24">
              <img
                src="/assets/images/image-bg.svg"
                className="max-w-xs w-3/5 px-4 opacity-20 mx-auto mb-6
                    "
                alt="No items found"
              />
              <p className="opacity-50 text-center">
                {t(`Spike Pie Generator.no-image`)}
              </p>
            </div>
          </div>
        )}
      </IonContent>
      <IonFooter>
        <IonToolbar className="!px-0">
          <div className="flex gap-2 px-2 max-w-7xl mx-auto justify-between">
            <IonButton
              disabled={disabled}
              className="font-semibold max-w-xs w-full"
              expand="block"
              onClick={() => {
                present({
                  swipeToClose: true,
                });
              }}
              color="secondary"
            >
              {t(`Spike Pie Generator.Customize...`)}
            </IonButton>
            <IonButton
              disabled={disabled}
              color="primary"
              onClick={() => {
                showLoading();
                setTimeout(() => {
                  handleGenerate();
                }, 300);
              }}
              className="font-semibold max-w-xs w-full"
              expand="block"
            >
              {t(`Generate`)}
            </IonButton>
          </div>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default SpikeGen;
